import { ApiResult } from 'src/Libs/api'
import {
  BasicSystemResponseModel,
  PowerMeterModalModelResponse,
  PowerMeterElements,
  RetrofitBatteryModalResponse,
  SystemConfigurationComponentsResponse,
  TransformerModalModelResponse,
} from '../models'
import { ShopAuthFetchWithResponse } from '../types'
import { StatusCodes } from '@e3dc-react/shell/Libs/http-status-codes'

/*
 * This endpoint gets the Basic System Configurators (Products).
 */
export const fetchBasicSystemConfiguration = async (shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<BasicSystemResponseModel>> => {
  const payload = {
    includes: {
      e3dc_connector_system_configurator: ['id', 'productionGroup', 'name', 'mainImage', 'translated', 'fixFlex', 'description'],
      media: ['url', 'thumbnails'],
      media_thumbnail: ['width', 'height', 'url'],
    },
  }

  try {
    const response = await shopFetch<BasicSystemResponseModel>({
      path: 'e3dc/system-configurator',
      method: 'GET',
      criteria: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<BasicSystemResponseModel>(response.data, true)
    }

    return new ApiResult<BasicSystemResponseModel>(null)
  } catch (error) {
    return new ApiResult<BasicSystemResponseModel>(null)
  }
}

/*
 * This endpoint will take all the Products (Components) based on the Basic System Configurator ID
 */
export const fetchSystemConfigurationComponents = async (
  configuratorId: string,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<SystemConfigurationComponentsResponse>> => {
  const payload = {
    associations: {
      smartMeterTypes: {
        associations: {
          product: {},
        },
      },
      components: {
        associations: {
          products: {
            associations: {
              product: {
                associations: {
                  media: {
                    associations: {
                      media: {
                        associations: {
                          thumbnails: {},
                        },
                      },
                    },
                  },
                  cover: {
                    associations: {
                      media: {
                        associations: {
                          thumbnails: {},
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    includes: {
      e3dc_connector_system_configurator: [
        'id',
        'productionGroup',
        'name',
        'mainImage',
        'translated',
        'components',
        'powerMeterExternal',
        'powerMeterFarming',
        'powerMeterHouseConnection',
        'smartMeterTypes',
      ],
      media: ['url', 'thumbnails'],
      media_thumbnail: ['width', 'height', 'url'],
      product_media: ['media', 'url', 'thumbnails'],
      e3dc_connector_system_configurator_component: ['products', 'mandatory', 'name', 'translated', 'id', 'position'],
      product: [
        'e3dcData',
        'media',
        'translated',
        'id',
        'calculatedPrice',
        'name',
        'e3dcPriceApplied',
        'cover',
        'minPurchase',
        'maxPurchase',
        'e3dcAvailability',
        'customFields',
      ],
      calculated_price: ['listPrice', 'unitPrice'],
      e3dc_connector_system_configurator_component_product: ['position', 'product'],
    },
  }
  try {
    const response = await shopFetch<SystemConfigurationComponentsResponse>({
      path: `e3dc/system-configurator/${configuratorId}`,
      method: 'GET',
      criteria: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<SystemConfigurationComponentsResponse>(response.data, true)
    }

    return new ApiResult<SystemConfigurationComponentsResponse>(null)
  } catch (error) {
    return new ApiResult<SystemConfigurationComponentsResponse>(null)
  }
}

/*
 * This will get all the Power Meters on the System Configurator
 */
export const fetchShopPowerMeters = async (powerMeterIds: string[], shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<PowerMeterElements>> => {
  const powerMetersPayload = {
    associations: {
      media: {},
    },
    filter: [
      {
        type: 'equalsAny',
        field: 'id',
        value: powerMeterIds,
      },
    ],
  }

  try {
    const response = await shopFetch<PowerMeterElements>({
      path: 'product',
      method: 'GET',
      criteria: powerMetersPayload,
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<PowerMeterElements>(response.data, true)
    }

    return new ApiResult<PowerMeterElements>(null, false)
  } catch (error) {
    return new ApiResult<PowerMeterElements>(null, true)
  }
}

/*
 * This Endpoint Get the List of available Power Meters based on the Power Meter Id
 */
export const fetchPreSelectedPowerMeter = async (
  powerMeterId: string,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<PowerMeterModalModelResponse>> => {
  const payload = {
    filter: [
      {
        type: 'multi',
        operator: 'and',
        queries: [
          {
            type: 'equals',
            field: 'e3dcData.productType',
            value: 'POWER_METER',
          },
          {
            type: 'equals',
            field: 'e3dcData.customFields.powerMeterId',
            value: powerMeterId,
          },
        ],
      },
    ],
    includes: {},
    associations: {
      media: {
        associations: {
          media: {},
        },
      },
    },
  }

  try {
    const response = await shopFetch<PowerMeterModalModelResponse>({
      path: 'product',
      method: 'GET',
      criteria: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<PowerMeterModalModelResponse>(response.data, true)
    }

    return new ApiResult<PowerMeterModalModelResponse>(null)
  } catch (error) {
    return new ApiResult<PowerMeterModalModelResponse>(null)
  }
}

/*
 * This endpoint will get list of all Transformers
 */
export const fetchCurrentTransformers = async (shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<TransformerModalModelResponse>> => {
  const payload = {
    associations: {
      media: {
        associations: {
          media: {
            associations: {
              thumbnails: {},
            },
          },
        },
      },
    },
    filter: [
      {
        type: 'equals',
        field: 'e3dcData.productType',
        value: 'POWER_CONVERTER',
      },
    ],

    /*includes: {
          product_listing: ['total', 'elements'],
          product: ['id', 'e3dcPriceApplied', 'available', 'name', 'cover', 'translated', 'calculatedPrice'],
          product_media: ['media'],
          calculated_price: ['calculatedPrice', 'listPrice', 'unitPrice'],
          cart_list_price: ['price', 'discount'],
          media: ['url', 'thumbnails'],
          media_thumbnail: ['width', 'height', 'url'],
        },*/
  }

  try {
    const response = await shopFetch<TransformerModalModelResponse>({
      path: 'product',
      method: 'GET',
      criteria: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<TransformerModalModelResponse>(response.data, true)
    }

    return new ApiResult<TransformerModalModelResponse>(null)
  } catch (error) {
    return new ApiResult<TransformerModalModelResponse>(null)
  }
}

/*
 * This endpoint will get list of all Retrofit Battreies
 */
export const fetchRetrofitBattery = async (
  batteryId: string,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<RetrofitBatteryModalResponse>> => {
  const payload = {
    associations: {
      manufacturer: {},
      media: {
        associations: {
          media: {
            associations: {
              thumbnails: {},
            },
          },
        },
      },

      properties: {
        associations: {
          group: {},
        },
      },
    },
    includes: {
      e3dc_connector_system_configurator: ['id', 'productionGroup', 'name', 'mainImage', 'translated', 'components'],
      media: ['url', 'thumbnails'],
      media_thumbnail: ['width', 'height', 'url'],
      product_media: ['media', 'url', 'thumbnails'],
      e3dc_connector_system_configurator_component: ['products', 'mandatory', 'name', 'translated', 'id'],
      product: ['e3dcData', 'media', 'translated', 'id', 'calculatedPrice', 'name', 'cover'],
    },
  }

  try {
    const response = await shopFetch<RetrofitBatteryModalResponse>({
      path: `product/${batteryId}`,
      method: 'GET',
      criteria: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<RetrofitBatteryModalResponse>(response.data, true)
    }

    return new ApiResult<RetrofitBatteryModalResponse>(null)
  } catch (error) {
    return new ApiResult<RetrofitBatteryModalResponse>(null)
  }
}

/*
 * This endpoint will return a System Configurator by the ID
 */
export const fetchSingleSystemConfiguration = async (
  configuratorId: string,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<BasicSystemResponseModel>> => {
  const payload = {
    includes: {
      e3dc_connector_system_configurator: ['id', 'productionGroup', 'name', 'mainImage', 'translated', 'fixFlex'],
      media: ['url', 'thumbnails'],
      media_thumbnail: ['width', 'height', 'url'],
    },
  }

  try {
    const response = await shopFetch<BasicSystemResponseModel>({
      path: `e3dc/system-configurator/${configuratorId}`,
      method: 'GET',
      criteria: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<BasicSystemResponseModel>(response.data, true)
    }

    return new ApiResult<BasicSystemResponseModel>(null)
  } catch (error) {
    return new ApiResult<BasicSystemResponseModel>(null)
  }
}
